import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api, { money } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Loader2, Wallet, TrendingUp, FolderKanban, Briefcase, Zap, ShieldAlert, ShieldCheck, ArrowRight, FileText, Calendar, PieChart as PieChartIcon, Brain,
} from "lucide-react";
import QuizDialog from "@/components/QuizDialog";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, AreaChart, Area, XAxis, YAxis, CartesianGrid, ComposedChart, Line, ReferenceDot } from "recharts";

const SECTOR_HEX = {
  "Énergie": "#EAB308", "Agriculture": "#16A34A", "Infrastructure": "#64748B",
  "Mines": "#64748B", "Technologie": "#9333EA", "Numérique": "#0891B2", "Immobilier": "#EA580C", "Santé": "#E11D48",
};

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${accent || "bg-congo-bluelight"}`}>
        <Icon className="h-5 w-5 text-congo-blue" />
      </div>
      <p className="text-sm text-slate-500 mt-3">{label}</p>
      <p className="font-head text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [period, setPeriod] = useState(12);
  const [quizOpen, setQuizOpen] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [evalStatus, setEvalStatus] = useState(null);

  useEffect(() => {
    api.get("/dashboard/me").then(({ data }) => setD(data));
    api.get("/quiz/me").then(({ data }) => { if (data && data.badge) setQuizResult(data); }).catch(() => {});
    api.get("/eval/status").then(({ data }) => setEvalStatus(data)).catch(() => {});
  }, []);
  if (!d) return <Layout><div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-congo-blue" /></div></Layout>;

  const timeline = d.returns_timeline || [];
  const cutoff = Date.now() - period * 30 * 24 * 3600 * 1000;
  const chartData = timeline.filter((p) => new Date(p.ts).getTime() >= cutoff);
  const hasChart = chartData.some((p) => p.realized != null || p.projected != null);

  const maturityByLabel = {};
  (d.maturities || []).forEach((m) => {
    if (!maturityByLabel[m.label]) maturityByLabel[m.label] = { items: [], total: 0 };
    maturityByLabel[m.label].items.push(m);
    maturityByLabel[m.label].total += m.interest;
  });
  const markers = chartData
    .filter((p) => maturityByLabel[p.label])
    .map((p) => ({ label: p.label, ymark: p.projected != null ? p.projected : p.realized }));

  const RTooltip = ({ active, label, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const realized = payload.find((x) => x.dataKey === "realized");
    const projected = payload.find((x) => x.dataKey === "projected");
    const mat = maturityByLabel[label];
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-md p-2.5 text-xs">
        <p className="font-semibold text-slate-800 mb-1">{label}</p>
        {realized && realized.value != null && <p className="text-green-700">Perçu : {money(realized.value)}</p>}
        {projected && projected.value != null && <p className="text-green-600">Projeté : {money(projected.value)}</p>}
        {mat && (
          <div className="mt-1.5 pt-1.5 border-t border-slate-100">
            <p className="font-semibold text-congo-golddark">Échéance(s) attendue(s)</p>
            {mat.items.map((m) => (<p key={`${m.project_title}-${m.interest}`} className="text-slate-600">{m.project_title} · +{money(m.interest)}</p>))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="font-head text-3xl font-bold text-slate-900">Bonjour, {user?.name?.split(" ")[0]} 👋</h1>
        <p className="text-slate-500 mt-1">Voici l'état de votre portefeuille d'investissements.</p>

        {user?.kyc_status !== "approved" && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between gap-4" data-testid="kyc-alert">
            <div className="flex items-center gap-3"><ShieldAlert className="h-6 w-6 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-800">Vérifiez votre identité pour débloquer l'investissement.</p></div>
            <Link to="/kyc"><Button size="sm" className="rounded-full bg-congo-blue whitespace-nowrap" data-testid="verify-kyc-btn">Vérifier <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <Stat icon={Wallet} label="Total investi" value={money(d.total_invested)} />
          <Stat icon={TrendingUp} label="Gains perçus" value={money(d.total_returns)} accent="bg-green-100" />
          <Stat icon={FolderKanban} label="Projets financés" value={d.projects_count} />
          <Stat icon={Briefcase} label="Emplois soutenus" value={d.impact.jobs} accent="bg-congo-goldlight" />
        </div>

        {d.sector_breakdown && d.sector_breakdown.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-8" data-testid="sector-breakdown">
            <h2 className="font-head text-lg font-semibold flex items-center gap-2"><PieChartIcon className="h-5 w-5 text-congo-blue" /> Répartition par secteur</h2>
            <div className="grid md:grid-cols-2 gap-6 mt-4 items-center">
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={d.sector_breakdown} dataKey="amount" nameKey="sector" cx="50%" cy="50%" outerRadius={95} innerRadius={55} paddingAngle={2}>
                      {d.sector_breakdown.map((e) => <Cell key={e.sector} fill={SECTOR_HEX[e.sector] || "#64748B"} />)}
                    </Pie>
                    <Tooltip formatter={(v) => money(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2.5" data-testid="sector-legend">
                {d.sector_breakdown.map((e) => {
                  const pct = d.total_invested ? Math.round((e.amount / d.total_invested) * 100) : 0;
                  return (
                    <div key={e.sector} className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-sm text-slate-700"><span className="h-3 w-3 rounded-full shrink-0" style={{ background: SECTOR_HEX[e.sector] || "#64748B" }} />{e.sector}</span>
                      <span className="text-sm font-semibold text-slate-900">{money(e.amount)} <span className="text-slate-400 font-normal">({pct}%)</span></span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-8" data-testid="returns-history">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-head text-lg font-semibold flex items-center gap-2"><TrendingUp className="h-5 w-5 text-green-600" /> Évolution des gains</h2>
            <div className="flex items-center gap-3">
              <span className="hidden sm:flex items-center gap-3 text-xs text-slate-500">
                <span className="flex items-center gap-1"><span className="h-1.5 w-4 rounded bg-green-600 inline-block" /> Perçu</span>
                <span className="flex items-center gap-1"><span className="h-1.5 w-4 rounded inline-block" style={{ background: "repeating-linear-gradient(90deg,#16A34A,#16A34A 4px,transparent 4px,transparent 7px)" }} /> Projeté</span>
                <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-congo-gold border border-white inline-block" /> Échéance</span>
              </span>
              <div className="flex bg-slate-100 rounded-full p-1" data-testid="period-selector">
                {[{ v: 3, l: "3M" }, { v: 6, l: "6M" }, { v: 12, l: "1A" }].map((o) => (
                  <button key={o.v} onClick={() => setPeriod(o.v)} data-testid={`period-${o.v}`}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${period === o.v ? "bg-congo-blue text-white" : "text-slate-600"}`}>{o.l}</button>
                ))}
              </div>
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-1">Gains perçus (plein) et rendements attendus jusqu'aux échéances (pointillés). Attendu total : <span className="font-semibold text-slate-700">{money(d.expected_total_returns)}</span></p>
          {!hasChart ? (
            <p className="text-sm text-slate-500 mt-4" data-testid="returns-empty">Vos gains apparaîtront ici dès vos premiers investissements et remboursements.</p>
          ) : (
            <div style={{ width: "100%", height: 300 }} className="mt-4">
              <ResponsiveContainer>
                <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gainGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#16A34A" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#16A34A" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
                  <Tooltip content={RTooltip} />
                  <Area type="monotone" dataKey="realized" name="realized" stroke="#16A34A" strokeWidth={2.5} fill="url(#gainGrad)" connectNulls={false} />
                  <Line type="monotone" dataKey="projected" name="projected" stroke="#16A34A" strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls={false} />
                  {markers.map((m) => (
                    <ReferenceDot key={m.label} x={m.label} y={m.ymark} r={5} fill="#FACC15" stroke="#ffffff" strokeWidth={2} ifOverflow="extendDomain" isFront />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-congo-blue to-congo-bluedark rounded-2xl p-6 mt-8 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="eval-card">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><ShieldCheck className="h-6 w-6 text-congo-gold" /></div>
            <div>
              <h2 className="font-head text-lg font-semibold">Évaluation investisseur</h2>
              {evalStatus?.can_invest ? (
                <>
                  <p className="text-blue-100 text-sm mt-0.5">Votre accès est activé : profil, test de connaissances et consentements à jour.</p>
                  <Badge className="mt-2 bg-green-500 text-white border-0" data-testid="eval-access-badge">Accès investisseur activé</Badge>
                </>
              ) : (
                <p className="text-blue-100 text-sm mt-0.5">Complétez votre profil et le test de connaissances (obligatoire avant d'investir).</p>
              )}
            </div>
          </div>
          {evalStatus?.can_invest ? (
            <Link to="/evaluation?mode=profile"><Button variant="outline" data-testid="update-profile-btn" className="rounded-full bg-transparent border-white/40 text-white hover:bg-white/10 whitespace-nowrap">Mettre à jour mon profil</Button></Link>
          ) : (
            <Link to="/evaluation"><Button data-testid="start-eval-btn" className="rounded-full bg-congo-gold text-slate-900 hover:bg-congo-golddark whitespace-nowrap font-semibold">Commencer l'évaluation</Button></Link>
          )}
        </div>

        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 mt-6 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="quiz-card">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><Brain className="h-6 w-6 text-congo-gold" /></div>
            <div>
              <h2 className="font-head text-lg font-semibold">Quiz rapide (bonus)</h2>
              <p className="text-slate-300 text-sm mt-0.5">Testez vos bases en 6 questions et gagnez un badge.</p>
              {quizResult && <Badge className="mt-2 bg-congo-gold text-slate-900 border-0" data-testid="quiz-last-score">Dernier score : {quizResult.score}/{quizResult.total} · {quizResult.badge}</Badge>}
            </div>
          </div>
          <Button onClick={() => setQuizOpen(true)} data-testid="start-quiz-btn" className="rounded-full bg-congo-gold text-slate-900 hover:bg-congo-golddark whitespace-nowrap font-semibold">{quizResult ? "Refaire le quiz" : "Faire le quiz"}</Button>
        </div>

        <QuizDialog open={quizOpen} onOpenChange={setQuizOpen} onDone={setQuizResult} />

        <div className="grid lg:grid-cols-3 gap-6 mt-8">
          {/* Portfolio */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
            <h2 className="font-head text-lg font-semibold flex items-center gap-2"><FileText className="h-5 w-5 text-congo-blue" /> Mes investissements</h2>
            {d.investments.length === 0 ? (
              <div className="text-center py-10" data-testid="empty-portfolio">
                <p className="text-slate-500">Vous n'avez pas encore investi.</p>
                <Link to="/projets"><Button className="mt-4 rounded-full bg-congo-blue" data-testid="explore-projects-btn">Explorer les projets</Button></Link>
              </div>
            ) : (
              <div className="mt-4 space-y-3" data-testid="investments-list">
                {d.investments.map((i) => (
                  <div key={i.id} className="flex items-center justify-between border border-slate-100 rounded-xl p-4">
                    <div>
                      <p className="font-medium text-slate-900">{i.project_title}</p>
                      <p className="text-xs text-slate-400 flex items-center gap-2 mt-1">
                        <span className="font-mono">{i.contract_id}</span> · <span className="capitalize">{i.payment_method}</span>
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-head font-bold text-slate-900">{money(i.amount)}</p>
                      <Badge className="bg-green-100 text-green-700 border-0 text-[10px]">Confirmé</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Repayments schedule */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h2 className="font-head text-lg font-semibold flex items-center gap-2"><Calendar className="h-5 w-5 text-congo-blue" /> Remboursements</h2>
            {d.repayments.length === 0 ? (
              <p className="text-sm text-slate-500 mt-4">Aucun remboursement pour le moment. Ils apparaîtront aux échéances.</p>
            ) : (
              <div className="mt-4 space-y-3" data-testid="repayments-list">
                {d.repayments.map((r) => (
                  <div key={r.id} className="border-b border-slate-100 pb-3">
                    <p className="text-sm font-medium text-slate-800">{r.project_title}</p>
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>Capital {money(r.capital)}</span>
                      <span className="text-green-600 font-semibold">+{money(r.interest)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-6 bg-congo-blue rounded-xl p-4 text-white">
              <Zap className="h-5 w-5 text-congo-gold" />
              <p className="text-sm mt-2 leading-relaxed">Votre impact : <span className="font-bold">{d.impact.jobs} emplois</span> et <span className="font-bold">{d.impact.energy_projects} projet(s) énergétique(s)</span> soutenus.</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
